"use client"

import { FormikProvider, useFormik } from "formik"
import { ChevronDown, Facebook, Instagram, Loader2, SlidersHorizontal } from "lucide-react"

import { FormInput } from "@/components/form-inputs"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useImportKnowledgeBaseFromSocialMutation } from "@/hooks/mutations/knowledge-base-social-import"
import {
  getSocialPlatform,
  SocialProfileUrlSchema,
} from "@/lib/ai/knowledge-base/social-import/url"
import {
  getKbSocialImportMaxPosts,
  KB_SOCIAL_IMPORT_DEFAULT_POSTS,
  type KbSocialImportPlatform,
} from "@/lib/jobs/kb-social-import-shared"
import type { ImportKnowledgeBaseFromSocialResult } from "@/server-actions/knowledge-base-social-import"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type SocialImportValues = { profileUrl: string; maxPosts: number | "" }

type SocialImportFormProps = {
  isDefault?: boolean
  disabled: boolean
  onImported: (
    result: ImportKnowledgeBaseFromSocialResult,
    normalizedUrl: string,
  ) => void | Promise<void>
}

type SocialMediaImportFormProps = {
  isDefault?: boolean
  disabled: boolean
  onImported: (
    result: ImportKnowledgeBaseFromSocialResult,
    normalizedUrl: string,
    platform: KbSocialImportPlatform,
  ) => void | Promise<void>
}

type SocialImportFormContentProps = {
  isDefault?: boolean
  fixedPlatform: KbSocialImportPlatform | null
  disabled: boolean
  onImported: SocialMediaImportFormProps["onImported"]
}

export function SocialImportForm({
  isDefault = true,
  platform,
  disabled,
  onImported,
}: SocialImportFormProps & { platform: KbSocialImportPlatform }) {
  return (
    <SocialImportFormContent
      isDefault={isDefault}
      fixedPlatform={platform}
      disabled={disabled}
      onImported={(result, normalizedUrl) => onImported(result, normalizedUrl)}
    />
  )
}

export function SocialMediaImportForm({
  isDefault = true,
  disabled,
  onImported,
}: SocialMediaImportFormProps) {
  return (
    <SocialImportFormContent
      isDefault={isDefault}
      fixedPlatform={null}
      disabled={disabled}
      onImported={onImported}
    />
  )
}

function SocialImportFormContent({
  isDefault = true,
  fixedPlatform,
  disabled,
  onImported,
}: SocialImportFormContentProps) {

  const tUi = useTranslations("Onboarding.SocialImportForm")

  const mutation = useImportKnowledgeBaseFromSocialMutation()
  const formik = useFormik<SocialImportValues>({
    initialValues: {
      profileUrl: "",
      maxPosts: KB_SOCIAL_IMPORT_DEFAULT_POSTS,
    },
    validate: values => {
      const errors: Partial<Record<keyof SocialImportValues, string>> = {}
      if (!values.profileUrl.trim()) {
        errors.profileUrl = tUi("profileURLIsRequired")
      } else {
        const profileUrlError = getProfileUrlError(
          values.profileUrl,
          fixedPlatform,tUi
        )
        if (profileUrlError) errors.profileUrl = profileUrlError
      }

      const detectedPlatform = resolveSocialPlatform(values.profileUrl)
      const effectivePlatform = fixedPlatform ?? detectedPlatform
      const maxPostsLimit = effectivePlatform
        ? getKbSocialImportMaxPosts(effectivePlatform)
        : getKbSocialImportMaxPosts("instagram")
      if (
        typeof values.maxPosts !== "number" ||
        !Number.isInteger(values.maxPosts) ||
        values.maxPosts < 1 ||
        values.maxPosts > maxPostsLimit
      ) {
        errors.maxPosts = `Choose between 1 and ${maxPostsLimit} posts.`
      }
      return errors
    },
    onSubmit: async (values, helpers) => {
      const parsedUrl = SocialProfileUrlSchema.safeParse(values.profileUrl)
      const detectedPlatform = parsedUrl.success
        ? getSocialPlatform(parsedUrl.data)
        : null
      if (
        !parsedUrl.success ||
        !detectedPlatform ||
        (fixedPlatform && detectedPlatform !== fixedPlatform) ||
        typeof values.maxPosts !== "number"
      ) return
      try {
        const result = await mutation.mutateAsync({
          profileUrl: parsedUrl.data,
          maxPosts: values.maxPosts,
          isDefault,
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
        await onImported(result, parsedUrl.data, detectedPlatform)
      } catch {
        // withQueryHandling owns user-facing errors.
      }
    },
  })
  const detectedPlatform = resolveSocialPlatform(formik.values.profileUrl)
  const effectivePlatform = fixedPlatform ?? detectedPlatform
  const copy = getSocialImportCopy(fixedPlatform, tUi)
  const maxPostsLimit = effectivePlatform
    ? getKbSocialImportMaxPosts(effectivePlatform)
    : getKbSocialImportMaxPosts("instagram")
  const buttonLabel = getSocialImportButtonLabel(fixedPlatform, detectedPlatform, tUi)

  return (
    <Card className="overflow-hidden border-violet-200 bg-gradient-to-br from-violet-50/70 to-background">
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <CardContent className="grid gap-6 p-4 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,0.9fr)] lg:items-center">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-violet-200 bg-white text-violet-700">
                  {fixedPlatform === "instagram" ? (
                    <Instagram className="mr-1 h-3.5 w-3.5" />
                  ) : null}
                  {fixedPlatform === "facebook" ? (
                    <Facebook className="mr-1 h-3.5 w-3.5" />
                  ) : null}
                  {fixedPlatform === null ? (
                    <>
                      <Instagram className="mr-1 h-3.5 w-3.5" />
                      <Facebook className="mr-1 h-3.5 w-3.5" />
                    </>
                  ) : null}
                  {copy.platformLabel}
                </Badge>
              </div>
              <div>
                <h3 className="text-xl font-semibold">{copy.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {copy.description}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                <span key={tUi("videoAudioAndWordsEmbeddedInImagesAre")}>{tUi("videoAudioAndWordsEmbeddedInImagesAre")}</span></p>
            </div>
            <div className="space-y-3 rounded-lg border bg-background p-4 shadow-sm">
              <FormInput
                name="profileUrl"
                id={`${fixedPlatform ?? "social-media"}-profile-url`}
                label={copy.inputLabel}
                placeholder={copy.placeholder}
              />
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="group w-full justify-between px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
                  >
                    <span className="inline-flex items-center gap-2"><TranslationText>{tUi.rich("tag0Tag0AdvancedSettings", { tag0: () => (<SlidersHorizontal className="h-4 w-4" />) })}</TranslationText></span>
                    <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="space-y-1.5 rounded-md border bg-muted/30 p-3">
                    <Label htmlFor={`${fixedPlatform ?? "social-media"}-max-posts`}>
                      <span key={tUi("maximumRecentPosts")}>{tUi("maximumRecentPosts")}</span></Label>
                    <Input
                      id={`${fixedPlatform ?? "social-media"}-max-posts`}
                      type="number"
                      min={1}
                      max={maxPostsLimit}
                      value={formik.values.maxPosts}
                      aria-invalid={Boolean(formik.touched.maxPosts && formik.errors.maxPosts)}
                      onBlur={() => void formik.setFieldTouched("maxPosts", true, true)}
                      onChange={event => void formik.setFieldValue(
                        "maxPosts",
                        event.target.value === "" ? "" : Number(event.target.value),
                      )}
                    />
                    <p key={formik.touched.maxPosts && formik.errors.maxPosts
                        ? formik.errors.maxPosts
                        : tUi("defaultsToValue0MaximumValue1", { value0: String(KB_SOCIAL_IMPORT_DEFAULT_POSTS), value1: String(maxPostsLimit) })} className={
                      formik.touched.maxPosts && formik.errors.maxPosts
                        ? "text-xs font-medium text-destructive"
                        : "text-xs text-muted-foreground"
                    }>
                      {formik.touched.maxPosts && formik.errors.maxPosts
                        ? formik.errors.maxPosts
                        : tUi("defaultsToValue0MaximumValue1", { value0: String(KB_SOCIAL_IMPORT_DEFAULT_POSTS), value1: String(maxPostsLimit) })}
                    </p>
                  </div>
                </CollapsibleContent>
              </Collapsible>
              <Button
                type="submit"
                className="w-full"
                disabled={disabled || mutation.isPending || !formik.values.profileUrl.trim()}
              >
                {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                <span>{buttonLabel}</span>
              </Button>
            </div>
          </CardContent>
        </form>
      </FormikProvider>
    </Card>
  )
}

function getProfileUrlError(
  value: string,
  fixedPlatform: KbSocialImportPlatform | null,tUi: ReturnType<typeof useTranslations<"Onboarding.SocialImportForm">>
): string | null {
  const parsedUrl = SocialProfileUrlSchema.safeParse(value)
  if (parsedUrl.success) {
    const detectedPlatform = getSocialPlatform(parsedUrl.data)
    if (!fixedPlatform || detectedPlatform === fixedPlatform) return null
    return getSocialImportCopy(fixedPlatform, tUi).validationMessage
  }

  if (fixedPlatform) return getSocialImportCopy(fixedPlatform, tUi).validationMessage

  try {
    const url = new URL(value)
    getSocialPlatform(url)
    return tUi("invalidSocialProfile")
  } catch (error) {
    if (error instanceof TypeError) return tUi("invalidSocialProfile")
    return tUi("unsupportedSocialPlatform")
  }
}

function resolveSocialPlatform(value: string): KbSocialImportPlatform | null {
  const parsedUrl = SocialProfileUrlSchema.safeParse(value)
  return parsedUrl.success ? getSocialPlatform(parsedUrl.data) : null
}

function getSocialImportButtonLabel(
  fixedPlatform: KbSocialImportPlatform | null,
  detectedPlatform: KbSocialImportPlatform | null,tUi: ReturnType<typeof useTranslations<"Onboarding.SocialImportForm">>
): string {
  const platform = fixedPlatform ?? detectedPlatform
  switch (platform) {
    case "instagram":
      return tUi("importInstagram")
    case "facebook":
      return tUi("importFacebook")
    case null:
      return tUi("importSocialProfile")
    default: {
      const exhaustivePlatform: never = platform
      return exhaustivePlatform
    }
  }
}

function getSocialImportCopy(platform: KbSocialImportPlatform | null, tUi: ReturnType<typeof useTranslations<"Onboarding.SocialImportForm">>): {
  platformLabel: string
  inputLabel: string
  title: string
  description: string
  placeholder: string
  validationMessage: string
} {
  switch (platform) {
    case "instagram":
      return {
        platformLabel: "Instagram",
        inputLabel: tUi("instagramProfileURL"),
        title: tUi("learnFromInstagram"),
        description: tUi("importPublicProfileDetailsAndRecentPostCaptions"),
        placeholder: "https://www.instagram.com/your-business/",
        validationMessage: tUi("enterAnInstagramProfileURL"),
      }
    case "facebook":
      return {
        platformLabel: "Facebook",
        inputLabel: tUi("facebookProfileURL"),
        title: tUi("learnFromFacebook"),
        description: tUi("importPublicPageDetailsAndRecentPostCaptions"),
        placeholder: "https://www.facebook.com/your-business/",
        validationMessage: tUi("enterAFacebookPageOrProfileURL"),
      }
    case null:
      return {
        platformLabel: tUi("instagramOrFacebook"),
        inputLabel: tUi("instagramOrFacebookProfileURL"),
        title: tUi("learnFromSocialMedia"),
        description: tUi("importPublicProfileDetailsAndRecentPostCaptions2"),
        placeholder: "https://www.instagram.com/your-business/",
        validationMessage: tUi("invalidSocialProfile"),
      }
    default: {
      const exhaustivePlatform: never = platform
      return exhaustivePlatform
    }
  }
}
